import React, { useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { slugify } from "../../lib/utils";
import { useFormik } from "formik";
import { initialValues, validationSchemas } from "../../lib/forms-utils";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { TranslatedInput } from "../ui/translated-input";
import { TranslatedTextarea } from "../ui/translated-textarea";
import * as Checkbox from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { MdDelete } from "react-icons/md";

const CreateUpdateCategoryForm = ({
  category,
  onFormSubmission,
  isEditMode,
  btnLoading,
  subcategories,
}: any) => {
  // selected categories state,  they have to stay outside the formik form.
  // if we are updating there might be a selected subcategories, inside the category,
  // get their ids, and set them as selected subcategories state.

  // otherwise we are creating a new category, so the selected subcategories will be empty.

  const [selectedSubcategories, setSelectedSubcategories] = React.useState<any>(
    []
  );

  // Category lock state
  const [isLocked, setIsLocked] = React.useState<boolean>(false);
  const [password, setPassword] = React.useState<string>("");
  const [clearPassword, setClearPassword] = React.useState<boolean>(false);

  const extractSelectedSubcategories = (category: any) => {
    if (!category || !category.subcategories) {
      return [];
    }

    return category.subcategories.map((subcategory: any) => subcategory._id);
  };

  const formik = useFormik({
    initialValues: initialValues.categoryUpdate(category),
    validationSchema: validationSchemas.categoryCreate,
    enableReinitialize: true,

    onSubmit: (values) => {
      if (isEditMode) {
        const removedSubcategories = determineRemovedSubcategories();
        const newlySelectedSubcategories =
          determineNewlySelectedSubcategories();

        onFormSubmission({
          categoryId: category._id,
          categoryData: {
            ...values,
            isLocked,
            password: password || undefined,
            clearPassword,
          },
          selectedSubcategories: newlySelectedSubcategories, // only new.
          removedSubcategories,
        });
      } else {
        onFormSubmission({
          categoryData: {
            ...values,
            isLocked,
            password: password || undefined,
          },
          selectedSubcategories,
        });
      }
    },
  });

  const determineRemovedSubcategories = () => {
    // see what category.subcategories are not in selectedSubcategories
    // those are the removed subcategories.

    if (!category || !category.subcategories) {
      return [];
    }

    return category.subcategories
      .map((subcategory: any) => subcategory._id)
      .filter((id: string) => !selectedSubcategories.includes(id));
  };

  // for updating a category, we need to determine the newly selected subcategories
  const determineNewlySelectedSubcategories = () => {
    if (!category || !category.subcategories) {
      return [];
    }

    return selectedSubcategories.filter(
      (id: string) =>
        !category.subcategories
          .map((subcategory: any) => subcategory._id)
          .includes(id)
    );
  };

  const handleNameOnBlur = (e: any) => {
    const name = e.target.value;
    const slugifiedName = slugify(name);
    formik.setFieldValue("slug", slugifiedName);

    // handle original on blur
    formik.handleBlur(e);
  };

  const handleSlugBlur = (e: any) => {
    const value = e.target.value;
    const slugifiedValue = slugify(value);
    formik.setFieldValue("slug", slugifiedValue);

    // handle original on blur
    formik.handleBlur(e);
  };

  const hiddenInputFileUploaderRef = useRef<HTMLInputElement | null>(null);

  // upload an image
  const clickHiddenInput = () => {
    if (!hiddenInputFileUploaderRef.current) return;

    hiddenInputFileUploaderRef.current.click();
  };

  const handleCustomImageUpload = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      let imgObj = e.target.files[0];

      // initiate reader and convert blob to base64.
      let reader = new FileReader();
      reader.readAsDataURL(imgObj);

      reader.onload = (e) => {
        let imageUrl = e.target?.result;

        formik.setFieldValue("image", imageUrl);
      };
    }
  };

  useEffect(() => {
    if (isEditMode && category) {
      setSelectedSubcategories(extractSelectedSubcategories(category));
      // Initialize lock state from category
      if (category.isLocked) {
        setIsLocked(true);
      }
    }
  }, []);

  return (
    <>
      <div className="my-8 flex flex-col">
        <div className="w-full h-full  flex items-start gap-x-2 pt-2">
          <div className="my-2 w-2/4 flex flex-col items-start">
            <Label className="block text-body mb-2 text-sm px-1">
              Internal Name *
            </Label>

            <Input
              name="internalName"
              type="internalName"
              id="internalName"
              className="bg-transparent text-white placeholder"
              value={formik.values.internalName}
              onChange={formik.handleChange}
              onBlur={handleNameOnBlur}
              error={
                formik.touched.internalName && formik.errors.internalName
                  ? (formik.errors.internalName as string)
                  : ""
              }
            />
          </div>

          <div className="my-2 w-2/4 flex flex-col items-start">
            <Label className="block text-body mb-2 text-sm px-1">Slug *</Label>

            <Input
              name="slug"
              type="slug"
              id="slug"
              className="bg-transparent text-white placeholder"
              value={formik.values.slug}
              onChange={formik.handleChange}
              onBlur={handleSlugBlur}
              error={
                formik.touched.slug && formik.errors.slug
                  ? (formik.errors.slug as string)
                  : ""
              }
            />
          </div>
        </div>

        <div className="w-full h-full flex pt-2">
          <TranslatedInput
            name="displayName"
            id="displayName"
            placeholder="Display Name"
            columns={2}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            values={formik.values.displayName}
            errors={formik.errors.displayName}
            touched={formik.touched.displayName}
          />
        </div>

        <div className="w-full h-full pt-2 flex items-end">
          <TranslatedTextarea
            name="description"
            placeholder="Description"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            values={formik.values.description}
            errors={formik.errors.description}
            touched={formik.touched.description}
            columns={2}
            isEnglishMandatory={false}
          />
        </div>
      </div>

      {/* Subcategories selection list */}
      <div className="w-full h-full flex  items-start gap-x-2 pt-2 mb-10">
        <div className="w-1/2 flex flex-col items-start">
          <Label className="block text-body mb-2 text-sm px-1">
            Subcategories ({subcategories.length})
          </Label>

          <ScrollArea.Root className="w-2/3 relative h-[200px] overflow-hidden">
            <ScrollArea.Viewport className="w-full h-full py-2 px-2 relative rounded bg-borderColor">
              {subcategories.map((subcategory: any) => {
                const isSelected = selectedSubcategories.includes(
                  subcategory._id
                );

                return (
                  <div key={subcategory._id} className="flex items-center my-2">
                    <Checkbox.Root
                      className="bg-black w-[25px] h-[25px] rounded hover:text-primary focus:ring outline-none"
                      checked={isSelected}
                      onCheckedChange={(newValue) => {
                        if (newValue) {
                          setSelectedSubcategories([
                            ...selectedSubcategories,
                            subcategory._id,
                          ]);
                        } else {
                          setSelectedSubcategories(
                            selectedSubcategories.filter(
                              (id: string) => id !== subcategory._id
                            )
                          );
                        }
                      }}
                    >
                      <Checkbox.Indicator className="text-primary">
                        <CheckIcon />
                      </Checkbox.Indicator>
                    </Checkbox.Root>

                    <label htmlFor={subcategory._id} className="ml-2">
                      {subcategory.internalName}
                    </label>
                  </div>
                );
              })}
            </ScrollArea.Viewport>

            {/* Ensure scrollbar stays inside the scrollable box */}
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </div>

        <div className="w-1/2 h-full flex flex-col items-start">
          <input
            ref={hiddenInputFileUploaderRef}
            type="file"
            accept="image/*"
            id="hidden-input-upload"
            className="hidden"
            placeholder=""
            onChange={handleCustomImageUpload}
            onClick={(e: any) => (e.target.value = "")}
          />

          <Label className="block text-body mb-2 text-sm px-1">Image</Label>

          <div className="flex w-full h-full">
            <div className="w-1/2 flex flex-col items-start mt-10">
              <Button onClick={clickHiddenInput}>Upload Image</Button>
              <p className="ps-1 text  mt-2">
                Width to Hight Ratio must be 1/1.4
              </p>
            </div>

            <div className="relative ">
              {formik.values.image && (
                <div className="h-full w-full">
                  <img
                    src={formik.values.image}
                    alt="category"
                    className=" h-[200px] rounded"
                  />
                  <MdDelete
                    size={26}
                    className="absolute -top-2 -right-2 text-white bg-red-600 p-1 rounded-full cursor-pointer"
                    onClick={() => formik.setFieldValue("image", "")}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category Lock Section */}
      <div className="w-full h-full flex flex-col items-start gap-4 pt-2 mb-10 border-t border-borderColor pt-6">
        <Label className="block text-body mb-2 text-lg font-semibold">
          Category Lock Settings
        </Label>

        <div className="flex items-center gap-3">
          <Checkbox.Root
            className="bg-black w-[25px] h-[25px] rounded hover:text-primary focus:ring outline-none"
            checked={isLocked}
            onCheckedChange={(newValue) => {
              setIsLocked(newValue as boolean);
              if (!newValue) {
                setPassword("");
                if (isEditMode) {
                  setClearPassword(true);
                }
              } else {
                setClearPassword(false);
              }
            }}
          >
            <Checkbox.Indicator className="text-primary">
              <CheckIcon />
            </Checkbox.Indicator>
          </Checkbox.Root>
          <label className="text-sm">Lock this category with a password</label>
        </div>

        {isLocked && (
          <div className="w-full max-w-md flex flex-col gap-2">
            <Label className="block text-body text-sm px-1">
              Category Password (PIN) *
            </Label>
            <Input
              type="password"
              placeholder="Enter password to lock category"
              className="bg-transparent text-white"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="text-xs text-gray-400 px-1">
              Minimum 3 characters. Users will need this password to access the
              category.
            </p>
          </div>
        )}

        {isEditMode && category?.isLocked && !clearPassword && (
          <div className="flex items-center gap-3 mt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsLocked(false);
                setClearPassword(true);
                setPassword("");
              }}
            >
              Remove Category Lock
            </Button>
            <span className="text-xs text-gray-400">
              This will remove the password protection from this category
            </span>
          </div>
        )}
      </div>

      <div className="py-2 h-full w-full flex items-end justify-end ">
        <Button
          disabled={btnLoading}
          loading={btnLoading}
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          {isEditMode ? "Update Category" : "Create Category"}
        </Button>
      </div>
    </>
  );
};

export default CreateUpdateCategoryForm;
